import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Zap, ShieldCheck, Clock, Sparkles, CheckCircle2, ArrowRight,
  BarChart3, RefreshCw, Package, TrendingUp,
} from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  // If already logged in, skip landing and go straight to dashboard
  if (user) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Nav */}
      <nav className="border-b border-white/10 backdrop-blur-md sticky top-0 z-40 bg-slate-950/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-emerald-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-slate-950" />
            </div>
            <span className="text-xl font-black tracking-tight">Vyntric</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid="landing-login-link"
              className="px-4 py-2 text-sm font-bold text-slate-300 hover:text-white"
            >
              Entrar
            </Link>
            <Link
              to="/signup"
              data-testid="landing-signup-link"
              className="px-5 py-2 text-sm font-bold bg-emerald-500 text-slate-950 hover:bg-emerald-400 rounded-sm"
            >
              Testar 7 dias grátis
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-400 rounded-full border border-emerald-500/30 mb-6">
            <Sparkles className="w-3 h-3" />
            HUB Logístico + Fiscal para Marketplaces
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-none">
            Fature todos os seus <span className="text-emerald-400">pedidos</span><br />
            no piloto automático.
          </h1>
          <p className="mt-6 text-xl text-slate-300 leading-relaxed max-w-2xl">
            Conecte seu <b className="text-white">Bling ERP</b>, configure suas regras
            por marketplace (Shopee, Mercado Livre, Amazon, Magalu) e deixe o Vyntric
            emitir NF-e automaticamente 24h por dia — mesmo sem você estar logado.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/signup"
              className="px-8 py-4 text-base font-black uppercase tracking-wider bg-emerald-500 text-slate-950 hover:bg-emerald-400 rounded-sm inline-flex items-center gap-2 shadow-2xl shadow-emerald-500/30"
              data-testid="landing-hero-cta"
            >
              Começar 7 dias grátis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="text-sm text-slate-400">
              Sem cartão de crédito · Cancele quando quiser
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-white/10">
        <h2 className="text-4xl font-black tracking-tighter mb-12">
          Um sistema para acabar com o trabalho manual de faturamento.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Zap, title: "Faturamento Automático", desc: "Ative uma vez e o sistema aplica suas regras por marketplace + emite NF-e 24/7." },
            { icon: RefreshCw, title: "Retry Inteligente", desc: "SEFAZ caiu? O sistema retenta sozinho (5m → 15m → 45m → 2h → 6h). Você só é notificado se precisar." },
            { icon: ShieldCheck, title: "Zero Duplicação", desc: "Cada pedido é faturado uma única vez. Idempotência garantida no banco." },
            { icon: BarChart3, title: "Dashboard em Tempo Real", desc: "KPIs de vendas, top produtos, gráficos diários e mensais direto do Bling." },
            { icon: Clock, title: "Histórico 90 dias", desc: "Todos os pedidos faturados registrados por 3 meses com busca e filtros." },
            { icon: Package, title: "Multi-Marketplace", desc: "Shopee, Mercado Livre, Amazon, Magalu, Americanas, Netshoes, TikTok Shop e mais." },
          ].map((f) => (
            <div key={f.title} className="border border-white/10 bg-white/5 rounded-lg p-6 hover:border-emerald-500/30 transition">
              <f.icon className="w-8 h-8 text-emerald-400 mb-4" />
              <h3 className="text-lg font-black mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-white/10">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-black tracking-tighter">Plano único e transparente</h2>
          <p className="text-slate-400 mt-2">Pague só pelo que usar. Sem taxas escondidas.</p>
        </div>
        <div className="max-w-2xl mx-auto border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 to-slate-900 rounded-2xl p-10 shadow-2xl shadow-emerald-500/10">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-emerald-400 font-black">Plano Vyntric</div>
            <div className="mt-4 flex items-end justify-center gap-2">
              <span className="text-6xl font-black tracking-tighter">R$ 149</span>
              <span className="text-3xl font-black text-slate-400">,90</span>
              <span className="text-sm text-slate-400 self-center ml-1">/mês</span>
            </div>
            <div className="text-sm text-slate-400 mt-2">+ R$ 49,90 por Bling adicional</div>
          </div>
          <div className="mt-8 space-y-3">
            {[
              "1 Bling ERP incluído",
              "Faturamento automático 24/7",
              "Regras por marketplace",
              "Retry inteligente (SEFAZ)",
              "Histórico de 90 dias",
              "Dashboard em tempo real",
              "Suporte prioritário",
              "7 dias grátis, sem cartão",
            ].map((f) => (
              <div key={f} className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-200">{f}</span>
              </div>
            ))}
          </div>
          <Link
            to="/signup"
            className="mt-8 block text-center px-8 py-4 text-sm font-black uppercase tracking-wider bg-emerald-500 text-slate-950 hover:bg-emerald-400 rounded-sm"
            data-testid="landing-pricing-cta"
          >
            Começar 7 dias grátis
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-16 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Vyntric · HUB Logístico, Fiscal e Operacional
      </footer>
    </div>
  );
}
